import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import {
  Search, Building2, ListChecks, FileText, Factory,
  FlaskConical, Shield, X, Loader
} from 'lucide-react';

const TYPE_CONFIG = {
  facility: { label: 'Facilities', icon: Building2, color: '#0066FF', bg: '#EFF6FF' },
  task: { label: 'Tasks', icon: ListChecks, color: '#D97706', bg: '#FFFBEB' },
  source: { label: 'Sources', icon: Factory, color: '#7C3AED', bg: '#F5F3FF' },
  document: { label: 'Documents', icon: FileText, color: '#16A34A', bg: '#F0FDF4' },
  bact: { label: 'BACT/LAER/RACT', icon: FlaskConical, color: '#DC2626', bg: '#FEF2F2' },
  federal: { label: 'Federal Standards', icon: Shield, color: '#0066FF', bg: '#EFF6FF' },
};

const PRIORITY_COLORS = {
  critical: '#DC2626',
  high: '#EA580C',
  medium: '#CA8A04',
  low: '#16A34A'
};

const STATUS_COLORS = {
  completed: '#16A34A',
  'in_progress': '#0066FF',
  overdue: '#DC2626',
  pending: '#64748b'
};

function groupResults(results) {
  const grouped = {};
  results.forEach(r => {
    if (!grouped[r.type]) grouped[r.type] = [];
    grouped[r.type].push(r);
  });
  return grouped;
}

export default function GlobalSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  // ⌘K to open search
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const search = useCallback(async (q) => {
    if (!q || q.trim().length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get(`/search?q=${encodeURIComponent(q.trim())}`);
      setResults(res.data.results || []);
      setOpen(true);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setResults([]);
      setOpen(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(() => search(query), 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, search]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Keyboard navigation
  const allResults = results;
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') { setOpen(false); setQuery(''); inputRef.current?.blur(); }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex(i => Math.min(i + 1, allResults.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex(i => Math.max(i - 1, -1)); }
    if (e.key === 'Enter' && activeIndex >= 0 && allResults[activeIndex]) {
      handleSelect(allResults[activeIndex]);
    }
  };

  const handleSelect = (result) => {
    navigate(result.url);
    setQuery('');
    setOpen(false);
    setActiveIndex(-1);
    inputRef.current?.blur();
  };

  const grouped = groupResults(results);
  const hasResults = results.length > 0;

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      {/* Search input */}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}>
        <Search size={15} style={{ position: 'absolute', left: 12, color: '#94a3b8', pointerEvents: 'none' }} />
        <input
          ref={inputRef}
          type="text"
          placeholder="Search for anything..."
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); setActiveIndex(-1); }}
          onFocus={() => query.length >= 2 && setOpen(true)}
          onKeyDown={handleKeyDown}
          style={{
            width: '100%',
            padding: '8px 72px 8px 36px',
            border: '1px solid #E2E8F0',
            borderRadius: 8,
            fontSize: 13,
            fontFamily: 'var(--font-body)',
            outline: 'none',
            background: '#F8FAFC',
            color: '#0A1628',
            boxSizing: 'border-box',
            transition: 'border-color 0.15s',
          }}
          onFocus={e => { e.target.style.background = 'white'; e.target.style.borderColor = '#0066FF'; }}
          onBlur={e => { e.target.style.background = '#F8FAFC'; e.target.style.borderColor = '#E2E8F0'; }}
        />
        {/* ⌘K hint */}
        {!query && (
          <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center', gap: 3, pointerEvents: 'none' }}>
            <kbd style={{ height: 20, display: 'inline-flex', alignItems: 'center', color: '#94a3b8', background: 'white', border: '1px solid #E2E8F0', borderRadius: 4, padding: '0 5px', fontFamily: 'monospace', boxShadow: '0 1px 0 #CBD5E1', fontSize: 13 }}>&#8984;</kbd>
            <kbd style={{ height: 20, display: 'inline-flex', alignItems: 'center', color: '#94a3b8', background: 'white', border: '1px solid #E2E8F0', borderRadius: 4, padding: '0 5px', fontFamily: 'monospace', boxShadow: '0 1px 0 #CBD5E1', fontSize: 11 }}>K</kbd>
          </div>
        )}
        {loading && <Loader size={14} style={{ position: 'absolute', right: 10, color: '#94a3b8', animation: 'spin 0.8s linear infinite' }} />}
        {!loading && query && (
          <button onClick={() => { setQuery(''); setResults([]); setOpen(false); }}
            style={{ position: 'absolute', right: 8, background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'flex', padding: 2 }}>
            <X size={14} />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {open && query.length >= 2 && (
        <div ref={dropdownRef} style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
          background: 'white', border: '1px solid #E2E8F0', borderRadius: 10,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)', zIndex: 9999,
          maxHeight: 480, overflowY: 'auto'
        }}>
          {loading && results.length === 0 && (
            <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
              Searching...
            </div>
          )}

          {!loading && !hasResults && (
            <div style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
              No results for "<strong>{query}</strong>"
            </div>
          )}

          {hasResults && Object.entries(grouped).map(([type, items]) => {
            const cfg = TYPE_CONFIG[type] || TYPE_CONFIG.document;
            const Icon = cfg.icon;
            return (
              <div key={type}>
                {/* Group header */}
                <div style={{ padding: '8px 14px 4px', fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, borderTop: '1px solid #F1F5F9' }}>
                  {cfg.label}
                </div>
                {items.map((result, i) => {
                  const globalIndex = results.indexOf(result);
                  const isActive = globalIndex === activeIndex;
                  return (
                    <div key={result.id}
                      onMouseEnter={() => setActiveIndex(globalIndex)}
                      onClick={() => handleSelect(result)}
                      style={{
                        padding: '9px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
                        background: isActive ? '#F0F7FF' : 'white',
                        borderLeft: isActive ? '2px solid #0066FF' : '2px solid transparent',
                        transition: 'all 0.1s'
                      }}>
                      <div style={{ width: 28, height: 28, borderRadius: 7, background: cfg.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Icon size={14} color={cfg.color} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#0A1628', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {result.title}
                        </div>
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {result.subtitle}
                        </div>
                      </div>
                      {result.priority && (
                        <span style={{ fontSize: 10, fontWeight: 700, color: PRIORITY_COLORS[result.priority] || '#64748b', background: '#F8FAFC', padding: '2px 6px', borderRadius: 6, flexShrink: 0 }}>
                          {result.priority.toUpperCase()}
                        </span>
                      )}
                      {result.status && !result.priority && (
                        <span style={{ fontSize: 10, fontWeight: 700, color: STATUS_COLORS[result.status] || '#64748b', background: '#F8FAFC', padding: '2px 6px', borderRadius: 6, flexShrink: 0 }}>
                          {result.status.replace('_', ' ').toUpperCase()}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}

          {hasResults && (
            <div style={{ padding: '8px 14px', borderTop: '1px solid #F1F5F9', fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>
              {results.length} result{results.length !== 1 ? 's' : ''} · Press ↑↓ to navigate · Enter to select · Esc to close
            </div>
          )}
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
